// Banner.tsx

import React, { useState } from "react";
import clsx from "clsx";

interface BannerProps {
  /**
   * The main title or heading of the banner.
   * Can be a string or a React element.
   */
  title?: React.ReactNode;

  /**
   * The descriptive message of the banner.
   * Can be a string or a React element.
   */
  message?: React.ReactNode;

  /**
   * The variant of the banner to determine its style.
   * @default 'info'
   */
  variant?: "info" | "success" | "warning" | "error";

  /**
   * Optional action buttons to display within the banner.
   */
  actions?: React.ReactNode;

  /**
   * Determines if the banner is dismissible.
   * @default false
   */
  dismissible?: boolean;

  /**
   * Callback function when the banner is dismissed.
   */
  onDismiss?: () => void;

  /**
   * Additional CSS classes for custom styling.
   */
  className?: string;

  /**
   * Flag to determine if text should be centered.
   * @default false
   */
  centerText?: boolean;
}

const variantStyles: Record<NonNullable<BannerProps["variant"]>, string> = {
  info: "bg-blue-100 text-blue-700",
  success: "bg-green-100 text-green-700",
  warning: "bg-yellow-100 text-yellow-700",
  error: "bg-red-100 text-red-700"
};

const Banner: React.FC<BannerProps> = ({
  title,
  message,
  variant = "info",
  actions,
  dismissible = false,
  onDismiss,
  className,
  centerText = false
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(true);

  const handleDismiss = () => {
    setIsVisible(false);
    if (onDismiss) {
      onDismiss();
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={clsx(
        "relative w-full p-4", // Add 'relative' for absolute positioning
        variantStyles[variant],
        "flex flex-col sm:flex-row items-center sm:items-start justify-center sm:justify-between",
        "space-y-2 sm:space-y-0 sm:space-x-4",
        "rounded-md shadow-md",
        className
      )}
      role="alert">
      {dismissible && (
        <button
          onClick={handleDismiss}
          className="absolute right-2 top-2 text-xl font-semibold leading-none text-current hover:text-gray-700 focus:outline-none bg-transparent p-1 rounded-full focus:ring-2 focus:ring-offset-2 focus:ring-blue-400"
          aria-label="Dismiss">
          &times;
        </button>
      )}
      <div
        className={clsx("flex-1 min-w-0", centerText ? "text-center sm:text-left" : "text-left")}>
        <div className="text-lg font-semibold">{title}</div>
        <div className="mt-1 text-sm">{message}</div>
      </div>
      <div
        className={clsx(
          "flex items-center space-x-2",
          centerText ? "justify-center sm:justify-end w-full sm:w-auto" : ""
        )}>
        {actions}
      </div>
    </div>
  );
};

export default Banner;
